import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Star,
  Crown,
  X,
  School,
  CheckCircle,
  ChevronRight,
  AlertTriangle,
  Shield,
  PanelLeftClose
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSubscription } from '../../contexts/SubscriptionContext'
import { useAuth } from '../../contexts/AuthContext'
import { useSidebar } from '../../contexts/SidebarContext'
import Logo from '../common/Logo'
import ComingSoonBadge from '../ui/ComingSoonBadge'
import { sidebarGroups, useSidebarNavigation, type SidebarItem } from './sidebarNav'
import SidebarRail from './SidebarRail'
import { SIDEBAR_EXPANDED_WIDTH, SIDEBAR_RAIL_WIDTH } from './sidebarState'

/**
 * Sidebar de navegação do app. O estado (drawer, preferência recolhida,
 * override do editor, peek) vive em SidebarContext; aqui é só a casca.
 */
const Sidebar: React.FC = () => {
  const { mode, drawerOpen, closeDrawer, toggle, startPeek, endPeek } = useSidebar()

  // Esc fecha o peek mesmo sem foco dentro da sidebar (peek por mouse)
  useEffect(() => {
    if (mode !== 'peek') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') endPeek()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mode, endPeek])

  if (mode === 'drawer') {
    return (
      <>
        <AnimatePresence>
          {drawerOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-ink-900/50 z-40"
              onClick={closeDrawer}
            />
          )}
        </AnimatePresence>
        <aside
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-surface dark:bg-ink-700 border-r border-cream-400 dark:border-ink-500 flex flex-col transform transition-transform duration-300 ease-in-out ${
            drawerOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarPanel
            onNavigate={closeDrawer}
            headerAction={
              <button
                type="button"
                onClick={closeDrawer}
                aria-label="Fechar menu"
                className="p-2 hover:bg-cream-100 dark:hover:bg-ink-700 rounded-caderno-sm"
              >
                <X className="w-5 h-5 text-ink-400 dark:text-ink-300" />
              </button>
            }
          />
        </aside>
      </>
    )
  }

  // Desktop: o wrapper reserva a largura no fluxo (64 ou 256); o <aside>
  // absoluto dentro dele é o que aparece — no peek ele tem 256 dentro de um
  // wrapper de 64, então cobre o conteúdo sem empurrá-lo.
  const expanded = mode === 'expanded'
  const showPanel = expanded || mode === 'peek'

  return (
    <motion.div
      className="relative shrink-0"
      initial={false}
      animate={{ width: expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_RAIL_WIDTH }}
      transition={{ duration: 0.2 }}
      onMouseEnter={expanded ? undefined : () => startPeek()}
      onMouseLeave={expanded ? undefined : endPeek}
      onFocus={expanded ? undefined : () => startPeek(0)}
      onBlur={(e) => {
        if (!expanded && !e.currentTarget.contains(e.relatedTarget as Node | null)) endPeek()
      }}
    >
      <motion.aside
        data-mode={mode}
        initial={false}
        animate={{ width: showPanel ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_RAIL_WIDTH }}
        transition={{ duration: 0.15 }}
        className={`absolute inset-y-0 left-0 z-50 bg-surface dark:bg-ink-700 border-r border-cream-400 dark:border-ink-500 flex flex-col overflow-hidden ${
          mode === 'peek' ? 'shadow-caderno-lg' : ''
        }`}
      >
        {showPanel ? (
          <SidebarPanel
            onNavigate={endPeek}
            headerAction={
              <button
                type="button"
                onClick={toggle}
                aria-label="Recolher navegação"
                aria-expanded={true}
                className="p-2 rounded-caderno-sm text-ink-400 hover:text-ink-700 hover:bg-cream-100 dark:hover:bg-ink-700 dark:hover:text-cream-300 transition-colors"
              >
                <PanelLeftClose className="w-5 h-5" />
              </button>
            }
          />
        ) : (
          <SidebarRail />
        )}
      </motion.aside>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Painel expandido (mesmo markup de sempre)
// ---------------------------------------------------------------------------

interface SidebarPanelProps {
  /** Roda depois de navegar: fecha o drawer (mobile) ou o peek (desktop) */
  onNavigate: () => void
  /** Botão no canto direito do cabeçalho (fechar drawer / recolher) */
  headerAction?: React.ReactNode
}

export const SidebarPanel: React.FC<SidebarPanelProps> = ({ onNavigate, headerAction }) => {
  const { isAdmin, profile } = useAuth()
  const hasSchool = !!profile?.school_id
  const navigate = useNavigate()
  const { isActive, isItemActive, isDisabledForNonAdmin, hasAccess, navigateTo } = useSidebarNavigation(onNavigate)
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null)

  const handleItemClick = (item: SidebarItem) => {
    if (item.children) {
      setOpenSubmenuId(openSubmenuId === item.id ? null : item.id)
      if (item.path) navigateTo(item.path, item.premium, item.adminOnly)
    } else if (item.path) {
      navigateTo(item.path, item.premium, item.adminOnly)
    }
  }

  return (
    <>
      {/* Header */}
      <div className="p-6 border-b border-cream-400 dark:border-ink-500">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Símbolo decorativo: o wordmark ao lado já anuncia o nome */}
            <Logo variant="symbol" size={40} decorative />
            <div>
              <h1 className="leading-none">
                <Logo variant="wordmark" size={17} />
              </h1>
              <p className="font-mono text-xs uppercase tracking-wider text-ink-400 dark:text-ink-300 mt-1">Plataforma Educacional</p>
            </div>
          </div>
          {headerAction}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto" aria-label="Navegação principal">
        {sidebarGroups.map((group, groupIdx) => (
          <div key={group.id} className={groupIdx > 0 ? 'mt-6' : ''}>
            <div className="font-mono text-xs uppercase tracking-[0.14em] text-ink-400 dark:text-ink-300 px-2.5 py-1.5">
              {group.label}
            </div>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const IconComponent = item.icon
                const disabled = isDisabledForNonAdmin(item)
                const access = hasAccess(item)
                const itemIsActive = isItemActive(item)
                const isParentActive = item.children?.some(child => isActive(child.path)) ?? false
                const isSubmenuOpen = openSubmenuId === item.id || isParentActive

                return (
                  <div key={item.id}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(item)}
                      disabled={disabled}
                      className={`w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-caderno-sm transition-all duration-200 text-left group relative text-sm ${
                        itemIsActive
                          ? 'bg-ink-900 text-cream-100 dark:bg-cream-100 dark:text-ink-900 font-semibold'
                          : disabled
                            ? 'text-ink-300 dark:text-ink-300 cursor-not-allowed opacity-60'
                            : access
                              ? 'text-ink-700 dark:text-cream-300 hover:bg-cream-200 dark:hover:bg-ink-700/50 font-medium'
                              : 'text-ink-300 dark:text-ink-300 hover:bg-cream-200 dark:hover:bg-ink-700/50'
                      }`}
                    >
                      <IconComponent
                        className={`w-5 h-5 ${
                          itemIsActive
                            ? 'text-cream-100 dark:text-ink-900'
                            : disabled
                              ? 'text-ink-300 dark:text-ink-300'
                              : access
                                ? 'text-ink-400 dark:text-ink-300 group-hover:text-ink-700 dark:group-hover:text-cream-100'
                                : 'text-ink-300 dark:text-ink-300'
                        }`}
                      />
                      <span className="flex-1 font-medium">{item.label}</span>

                      {disabled && <ComingSoonBadge size="sm" variant="subtle" />}

                      {!disabled && item.premium && !access && (
                        <Crown className="w-4 h-4 text-ochre-500" />
                      )}

                      {item.badge && (
                        <span className="bg-blood-100 dark:bg-blood-500/30 text-blood-500 text-xs px-2 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      )}
                      {item.children && (
                        <motion.div animate={{ rotate: isSubmenuOpen ? 90 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronRight className="w-4 h-4 text-ink-400 dark:text-ink-300" />
                        </motion.div>
                      )}
                    </button>

                    <AnimatePresence>
                      {item.children && isSubmenuOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden pl-8 pr-4 py-1 space-y-1"
                        >
                          {item.children.map((subItem) => {
                            const SubIconComponent = subItem.icon
                            const subActive = isActive(subItem.path)
                            return (
                              <button
                                key={subItem.id}
                                type="button"
                                onClick={() => navigateTo(subItem.path || '')}
                                className={`w-full flex items-center space-x-2.5 px-2.5 py-1.5 rounded-caderno-sm transition-all duration-200 text-left group text-xs ${
                                  subActive
                                    ? 'bg-ink-900 text-cream-100 dark:bg-cream-100 dark:text-ink-900 font-semibold'
                                    : 'text-ink-700 dark:text-cream-300 hover:bg-cream-200 dark:hover:bg-ink-700/50 font-medium'
                                }`}
                              >
                                <SubIconComponent
                                  className={`w-4 h-4 ${
                                    subActive
                                      ? 'text-cream-100 dark:text-ink-900'
                                      : 'text-ink-400 dark:text-ink-300 group-hover:text-ink-700 dark:group-hover:text-cream-100'
                                  }`}
                                />
                                <span className="flex-1">{subItem.label}</span>
                              </button>
                            )
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {/* Área da escola — qualquer membro, não só a coordenação */}
        {hasSchool && (
          <>
            <div className="border-t border-cream-400 dark:border-ink-500 my-4"></div>
            <button
              type="button"
              onClick={() => navigateTo('/app/escola')}
              className={`w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-caderno-sm transition-all duration-200 text-left group text-sm ${
                isActive('/app/escola')
                  ? 'bg-ink-900 text-cream-100 dark:bg-cream-100 dark:text-ink-900 font-semibold'
                  : 'text-ink-700 dark:text-cream-300 hover:bg-cream-200 dark:hover:bg-ink-700/50 font-medium'
              }`}
            >
              <School className={`w-5 h-5 ${isActive('/app/escola') ? 'text-cream-100 dark:text-ink-900' : 'text-ink-400 dark:text-ink-300 group-hover:text-ink-700 dark:group-hover:text-cream-100'}`} />
              <span className="flex-1">Minha escola</span>
            </button>
          </>
        )}

        {/* Admin Panel Link */}
        {isAdmin && (
          <>
            <div className="border-t border-cream-400 dark:border-ink-500 my-4"></div>
            <button
              type="button"
              onClick={() => { navigate('/admin'); onNavigate() }}
              className={`w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-caderno-sm transition-all duration-200 text-left group text-sm ${
                isActive('/admin')
                  ? 'bg-blood-500 text-cream-50 font-semibold'
                  : 'text-ink-700 dark:text-cream-300 hover:bg-cream-200 dark:hover:bg-ink-700/50 font-medium'
              }`}
            >
              <Shield className={`w-5 h-5 ${isActive('/admin') ? 'text-cream-50' : 'text-ink-400 dark:text-ink-300 group-hover:text-ink-700 dark:group-hover:text-cream-100'}`} />
              <span className="flex-1">Painel de Admin</span>
            </button>
          </>
        )}
      </nav>

      <SubscriptionStatusCard onNavigate={onNavigate} />
    </>
  )
}

// ---------------------------------------------------------------------------
// Card de status da assinatura (rodapé do painel expandido)
// ---------------------------------------------------------------------------

const SubscriptionStatusCard: React.FC<{ onNavigate: () => void }> = ({ onNavigate }) => {
  const navigate = useNavigate()
  const { subscriptionPhase, daysLeftInTrial, schoolAccess } = useSubscription()
  const { isSchoolAdmin } = useAuth()

  // Formatação inline de propósito: os helpers de data da área escolar vivem em
  // `hooks/school/`, que importa o cliente Supabase — trazê-lo para o layout
  // arrastaria o cliente para o grafo de imports da navegação.
  const graceDate = schoolAccess?.graceUntil
    ? new Date(schoolAccess.graceUntil).toLocaleDateString('pt-BR')
    : null

  const goToBilling = () => {
    navigate('/app/billing')
    onNavigate()
  }

  const daysLeft = daysLeftInTrial ?? 0

  return (
    <div className="p-4 mt-auto">
      {isSchoolAdmin || subscriptionPhase === 'escolar' ? (
        <div className="bg-terra-50 dark:bg-terra-500/10 text-terra-700 dark:text-terra-300 rounded-caderno-md p-4 text-center">
          <School className="w-6 h-6 text-terra-500 mx-auto mb-2" />
          <h3 className="font-display font-semibold text-lg mb-1">Seu Plano: Escolar</h3>
          {/* Antes eram dois cartões idênticos, um deles titulado "Escolar
              (Admin)" — o papel não é parte do nome do plano. */}
          <p className="text-sm text-ink-700 dark:text-cream-300">
            {schoolAccess
              ? <>Seu acesso vem de <span className="font-medium">{schoolAccess.name}</span>.</>
              : 'Seu acesso vem do plano da sua instituição.'}
          </p>
          {/* Carência: a equipe mantém o acesso até a data, então o aviso é de
              prazo, não de perda já ocorrida. */}
          {schoolAccess?.status === 'past_due' && graceDate && (
            <p className="mt-2 text-sm text-ochre-700 dark:text-ochre-300">
              Pagamento pendente — acesso garantido até {graceDate}.
            </p>
          )}
          <button
            type="button"
            onClick={() => { navigate('/app/escola'); onNavigate() }}
            className="mt-3 text-sm font-medium text-terra-500 hover:text-terra-700 dark:text-terra-300 underline"
          >
            {isSchoolAdmin ? 'Gerenciar a escola' : 'Ver minha escola'}
          </button>
        </div>
      ) : subscriptionPhase === 'premium' ? (
        <div className="bg-moss-100 dark:bg-moss-500/20 text-moss-500 rounded-caderno-md p-4 text-center">
          <CheckCircle className="w-6 h-6 text-moss-500 mx-auto mb-2" />
          <h3 className="font-display font-semibold text-lg mb-1">Seu Plano: Premium</h3>
          <p className="text-sm text-ink-700 dark:text-cream-300">Você tem acesso a todos os recursos premium.</p>
        </div>
      ) : subscriptionPhase === 'trialing' ? (
        <div className="bg-ochre-100 dark:bg-ochre-500/20 text-ink-900 dark:text-cream-50 rounded-caderno-md p-4 text-center border-2 border-ochre-500/30">
          <Star className="w-6 h-6 text-ochre-500 mx-auto mb-2" />
          <h3 className="font-display font-semibold text-base mb-1">Período de Teste Ativo</h3>
          <div className="font-display text-2xl font-bold text-terra-500 dark:text-terra-300 mb-1">
            {daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}
          </div>
          <p className="text-xs text-ink-500 dark:text-ink-300 mb-3">Aproveite os recursos premium</p>
          <button type="button" onClick={goToBilling} className="w-full bg-terra-500 hover:bg-terra-700 text-cream-50 py-2 rounded-caderno-sm font-medium transition-colors text-sm">
            Assinar Agora
          </button>
        </div>
      ) : subscriptionPhase === 'trial_expired' ? (
        <div className="bg-blood-100 dark:bg-blood-500/20 text-blood-500 rounded-caderno-md p-4 text-center border-2 border-blood-500/30">
          <AlertTriangle className="w-6 h-6 text-blood-500 mx-auto mb-2" />
          <h3 className="font-display font-semibold text-base mb-1">Seu período de teste terminou</h3>
          <p className="text-xs text-ink-500 dark:text-ink-300 mb-3">Tudo que você criou continua salvo. Assine para voltar a criar avaliações.</p>
          <button type="button" onClick={goToBilling} className="w-full bg-blood-500 hover:bg-blood-500/90 text-cream-50 py-2 rounded-caderno-sm font-medium transition-colors text-sm">
            Ver Planos
          </button>
        </div>
      ) : (
        <div className="bg-terra-50 dark:bg-terra-500/10 text-terra-700 dark:text-terra-300 rounded-caderno-md p-4 text-center">
          <Crown className="w-6 h-6 text-terra-500 mx-auto mb-2" />
          <h3 className="font-display font-semibold text-lg mb-1">Escolha um plano</h3>
          <p className="text-sm text-ink-700 dark:text-cream-300 mb-4">Assine para criar avaliações e baixar PDFs sem limites.</p>
          <button type="button" onClick={goToBilling} className="w-full bg-terra-500 hover:bg-terra-700 text-cream-50 py-2 rounded-caderno-sm font-medium transition-colors">
            Ver Planos
          </button>
        </div>
      )}
    </div>
  )
}

export default Sidebar
